<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import sanitizeHtml from 'sanitize-html';
import type { PageData } from './Form.vue';
import type { SocialLinks } from './SocialLinks.vue';

interface FormData {
  id?: string;
  form_heading?: string;
  contact_form_json?: PageData;
  page_for_iframe?: string;
  text?: string;
}

interface ContactsData {
  title?: string;
  title_url?: string;
  content?: string;
}

interface LinksData {
  link?: {
    url: string;
    title: string;
    target: string;
  };
}

interface AsideData {
  heading?: string;
  contacts?: ContactsData[];
  links?: LinksData[];
  social_networks?: SocialLinks;
  maichimp_action_for_footer_form?: string;
}

const props = defineProps<{
  section: {
    form: FormData;
    aside: AsideData;
  };
}>();

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeHeight = ref('500px');

const updateIframeHeight = (event: MessageEvent) => {
  if (event.data && event.data.source === 'react-devtools-content-script') {
    return;
  }

  try {
    const data =
      typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (data && data.iframeHeight) {
      iframeHeight.value = `${data.iframeHeight}px`;
    }
    if (data && data.action === 'scrollToIframe') {
      const headerHeight = document.querySelector('#header')?.clientHeight || 0;
      const iframePosition = iframeRef.value?.getBoundingClientRect().top || 0;
      const scrollPosition = window.pageYOffset + iframePosition - headerHeight;
      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }
  } catch (error) {
    console.error('Parent: Error parsing message:', event.data, error);
  }
};

watch(
  () => props.section.form.page_for_iframe,
  () => {
    iframeHeight.value = '500px';
  }
);

onMounted(() => {
  window.addEventListener('message', updateIframeHeight);

  if (iframeRef.value) {
    setTimeout(() => {
      iframeRef.value?.contentWindow?.postMessage('getHeight', '*');
    }, 100);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', updateIframeHeight);
});
</script>

<template>
  <section class="relative z-10 overflow-hidden bg-white">
    <div class="container items-start md:flex lg:pt-2.5">
      <div :id="section.form.id" class="flex-1 py-12 md:py-30 md:pr-3">
        <h2
          v-if="section.form.form_heading"
          class="h5 mb-1 md:mb-5 lg:text-[64px]"
        >
          {{ section.form.form_heading }}
        </h2>
        <div
          v-if="section.form.text"
          class="mb-7.5 text-xl"
          v-html="sanitizeHtml(section.form.text)"
        />
        <!-- <div v-if="section.form.text" v-html="section.form.contact_form_html" /> -->
        <!-- <Form
          v-if="section?.form?.contact_form_json"
          :form="section.form.contact_form_json"
        /> -->
        <iframe
          ref="iframeRef"
          class="w-full transition-all duration-300"
          :src="section?.form?.page_for_iframe"
          :style="{ minHeight: iframeHeight }"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          frameborder="0"
          tabindex="0"
        ></iframe>
      </div>
      <aside
        v-if="section.aside"
        class="relative z-10 flex-shrink-0 bg-grad-cream py-12 before:absolute before:inset-y-0 before:-left-6 before:-z-10 before:w-dvw before:bg-grad-cream before:content-[''] md:ml-8 md:w-2/5 md:py-30 md:pl-6 md:before:-left-3 lg:pl-27 xl:ml-[135px]"
      >
        <h3
          v-if="section.aside.heading"
          class="-mt-0.5 border-b border-dotted border-current pb-10 text-3xl text-purple"
        >
          {{ section.aside.heading }}
        </h3>
        <div class="border-b border-dotted border-current py-9.5 first:pt-0">
          <div
            v-for="(contactItem, index) in section.aside.contacts"
            :key="index"
            class="mb-11 last:mb-0"
          >
            <h4
              v-if="contactItem.title && contactItem.title_url"
              class="h6 mb-8"
            >
              <NuxtLink
                :to="contactItem.title_url"
                class="group inline-flex flex-wrap decoration-black decoration-dotted decoration-1 underline-offset-8 hover:underline"
              >
                <span>{{ contactItem.title }}</span>
                <svg
                  class="ml-2 block h-4 w-3 self-center transition-transform duration-300 group-hover:translate-x-1"
                >
                  <use href="/icons/icons.svg#arrow-r" />
                </svg>
              </NuxtLink>
            </h4>
            <h4 v-else class="h6 mb-8">
              {{ contactItem.title }}
            </h4>
            <div
              v-if="contactItem.content"
              class="typography-address"
              v-html="
                sanitizeHtml(contactItem.content, {
                  allowedAttributes: false,
                  allowedTags: false,
                  allowVulnerableTags: true,
                  transformTags: {
                    script: () => ({
                      tagName: 'noscript',
                      attribs: {},
                    }),
                  },
                })
              "
            />
          </div>
        </div>
        <div class="border-b border-dotted border-current py-9.5">
          <ul class="flex flex-col">
            <li
              v-for="(linkItem, index) in section.aside.links"
              :key="index"
              class="border-top mb-5 border-dotted border-current last:mb-0"
            >
              <ButtonLink
                v-if="linkItem.link"
                v-bind="linkItem.link"
                icon="arrow-r"
              />
            </li>
          </ul>
        </div>
        <div class="pb-6 pt-9.5">
          <!-- <SubscribeFormMailchimp
            v-if="section.aside?.maichimp_action_for_footer_form"
            column
            :maichimp_action_for_footer_form="
              section.aside?.maichimp_action_for_footer_form
            "
          /> -->
          <SocialLinks
            v-if="section.aside?.social_networks"
            :data="section.aside?.social_networks"
            class="text-white"
          />
        </div>
      </aside>
    </div>
  </section>
</template>
